import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { WbTextField } from '../WbTextField';
import { ConfirmDialog } from '../ConfirmDialog';
import { KeyIcon, KeyOffIcon } from '../Icons';

const useStyles = makeStyles(theme => ({
  formRow: {
    display: 'flex',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 45,
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  saveIcon: {
    color: theme.palette.success.main,
  },
  cancelIcon: {
    color: theme.palette.error.main,
  },
}));

export type WbSensitiveKeyValueFieldEntry = {
  key: string;
  value: string;
  isSensitive: boolean;
};

export type WbSensitiveKeyValueFieldProps = {
  active: boolean;
  id?: string;
  editableKey: boolean;
  forceSensitiveValue?: boolean;
  configName?: string;
  keyHelperText?: string;
  keyPlaceholder?: string;
  valueHelperText?: string;
  sensitiveValueHelperText?: string;
  valuePlaceholder?: string;
  sensitiveValuePlaceholder?: string;
  initialValue: WbSensitiveKeyValueFieldEntry;
  keyPrefix?: string;
  actionsEnabled: boolean;
  isSaving: boolean;
  validator?: z.ZodObject<{
    key: z.ZodString;
    value: z.ZodString;
    isSensitive: z.ZodBoolean;
  }>;
  deleteDialogTitle?: string;
  deleteDialogConfirmBtnText?: string;
  deleteDialogContent?: React.ReactNode | React.ReactNode[];
  onStartEditMode?: () => any;
  onCancelEditMode?: (value: WbSensitiveKeyValueFieldEntry) => any;
  onDeleteAction?: () => any;
  onSaveAction?: (updatedValue: WbSensitiveKeyValueFieldEntry) => any;
  onValidate?: (
    value: WbSensitiveKeyValueFieldEntry,
  ) => { fieldName: 'key' | 'value'; error: string }[];
};

const defaultValidator = z.object({
  key: z.string(),
  value: z.string(),
  isSensitive: z.boolean(),
});

export const WbSensitiveKeyValueField = (
  props: WbSensitiveKeyValueFieldProps,
) => {
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    control,
    formState: { errors },
  } = useForm<WbSensitiveKeyValueFieldEntry>({
    mode: 'onSubmit',
    resolver: zodResolver(props.validator ?? defaultValidator),
    defaultValues: {
      ...props.initialValue,
      isSensitive: props.forceSensitiveValue
        ? true
        : props.initialValue.isSensitive,
    },
  });
  const classes = useStyles();
  const watchedValues = watch();
  const [showSensitiveValue, setShowSensitiveValue] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const onSubmit = (data: WbSensitiveKeyValueFieldEntry) => {
    const validationErrors = props.onValidate?.(data);
    if (validationErrors !== undefined && validationErrors.length > 0) {
      validationErrors.forEach(err => {
        setError(err.fieldName, { type: 'string', message: err.error });
      });
    } else {
      props.onSaveAction?.(data);
    }
  };

  const keyId = props.id ?? props.initialValue.key;

  const valueId = `value-${keyId}`;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formRow}>
          {props.editableKey ? (
            <FormControl style={{ flex: 1 }}>
              <Controller
                name="key"
                control={control}
                render={({ field }) => (
                  <WbTextField
                    id={keyId}
                    label={props.keyHelperText}
                    aria-invalid={errors.key ? true : false}
                    placeholder={props.keyPlaceholder}
                    disabled={!props.active}
                    error={errors.key ? true : false}
                    helperText={errors.key?.message}
                    value={field.value}
                    onChange={field.onChange}
                    InputProps={
                      props.keyPrefix
                        ? {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  style={{
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {props.keyPrefix}
                                </Typography>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                )}
              />
            </FormControl>
          ) : (
            <Box style={{ flex: 1 }}>
              <Typography>{props.configName}</Typography>
              <Typography variant="body2" color="textSecondary">
                {props.keyHelperText}
              </Typography>
            </Box>
          )}
          <Controller
            name="value"
            control={control}
            render={({ field }) => (
              <FormControl style={{ flex: 1 }}>
                <WbTextField
                  id={valueId}
                  label={
                    watchedValues.isSensitive && props.sensitiveValueHelperText
                      ? props.sensitiveValueHelperText
                      : props.valueHelperText
                  }
                  aria-invalid={errors.value ? true : false}
                  disabled={!props.active}
                  placeholder={
                    watchedValues.isSensitive && props.sensitiveValuePlaceholder
                      ? props.sensitiveValuePlaceholder
                      : props.valuePlaceholder
                  }
                  error={errors.value ? true : false}
                  type={
                    watchedValues.isSensitive && !showSensitiveValue
                      ? 'password'
                      : 'text'
                  }
                  helperText={errors.value?.message}
                  InputProps={{
                    endAdornment: props.forceSensitiveValue ? (
                      <IconButton
                        title="Show value"
                        aria-label="sensitive value visibility"
                        style={{
                          display: !props.active ? 'none' : 'initial',
                        }}
                        size="small"
                        onMouseDown={() => setShowSensitiveValue(true)}
                        onMouseUp={() => setShowSensitiveValue(false)}
                        onMouseLeave={() => setShowSensitiveValue(false)}
                      >
                        {showSensitiveValue ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    ) : (
                      <Checkbox
                        title="Toggle sensitivity"
                        inputProps={{ 'aria-label': 'sensitivity' }}
                        style={{ padding: 0 }}
                        disabled={!props.active}
                        size="small"
                        icon={<KeyOffIcon />}
                        checkedIcon={<KeyIcon />}
                        checked={watchedValues.isSensitive}
                        onChange={event => {
                          setValue('isSensitive', event.target.checked);
                        }}
                      />
                    ),
                  }}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
            )}
          />
          <Box
            className={classes.actionsContainer}
            style={props.active ? { display: 'none' } : {}}
          >
            <IconButton
              title="Edit"
              disabled={!props.actionsEnabled}
              type="button"
              aria-label="edit"
              color="secondary"
              size="small"
              onClick={() => {
                props.onStartEditMode?.();
                if (watchedValues.isSensitive) setValue('value', '');
              }}
            >
              <EditIcon color="inherit" />
            </IconButton>
            <IconButton
              title="Delete"
              disabled={!props.actionsEnabled}
              aria-label="delete"
              size="small"
              color="secondary"
              onClick={() => setShowDeleteDialog(true)}
            >
              <DeleteOutlinedIcon color="inherit" />
            </IconButton>
          </Box>
          <Box
            className={classes.actionsContainer}
            style={!props.active ? { display: 'none' } : {}}
          >
            <IconButton
              title="Save"
              disabled={props.isSaving}
              aria-label="save"
              size="small"
              type="submit"
              className={classes.saveIcon}
            >
              <CheckIcon color="inherit" />
            </IconButton>
            <IconButton
              color="secondary"
              title="Cancel"
              disabled={props.isSaving}
              aria-label="cancel"
              size="small"
              className={classes.cancelIcon}
              onClick={() => {
                reset();
                props.onCancelEditMode?.(props.initialValue);
              }}
            >
              <CloseIcon color="inherit" />
            </IconButton>
          </Box>
        </Box>
      </form>
      <ConfirmDialog
        title={props.deleteDialogTitle ?? 'Delete configuration'}
        description={
          props.deleteDialogContent ??
          'Are you sure you want to delete this configuration?'
        }
        open={showDeleteDialog}
        onConfirm={() => {
          props.onDeleteAction?.();
          setShowDeleteDialog(false);
        }}
        onClose={() => setShowDeleteDialog(false)}
        inProgress={false}
        confirmButtonText={props.deleteDialogConfirmBtnText ?? 'Delete'}
      />
    </>
  );
};
